// =====================================================================
// Assessment Center — Instructor Panel
// Grading API. Reads/writes the shared attempts store (see
// src/shared/grading/attempts-store.ts) rather than this panel's own
// exam-authoring mock data (lib/mock-api.ts), since reviewing an essay
// means seeing the exact attempt a student submitted.
//
// Includes attachments (images/PDFs) on each essay answer, and an
// explicit `publishGrade` action — reviewing an essay does not
// auto-publish the grade; the instructor does that deliberately once
// every essay on the attempt is reviewed.
// =====================================================================
import * as store from "../../../shared/grading/attempts-store";
import { reviewEssayAnswer, publishGrade as publishGradeService, allEssaysReviewedForAttempt } from "../../../shared/grading/grading-service";
import type { QuestionType, AttachmentFileType } from "../../../shared/types/db";

export interface PendingReviewAttempt {
  attempt_id: string;
  exam_id: string;
  exam_title: string;
  student_id: string;
  attempt_number: number;
  submitted_at: string | null;
  pending_essay_count: number;
  total_essay_count: number;
}

const delay = (ms = 220) => new Promise((r) => setTimeout(r, ms));

function essayExamQuestionsFor(examId: string) {
  return store
    .getExamSections(examId)
    .flatMap((s) => store.getExamQuestions(s.id))
    .filter((eq) => store.getQuestion(eq.question_id)?.type === "essay");
}

/** Every attempt still in "submitted" status — by design (see grading-engine's
 *  `statusAfterSubmission`) that only happens for exams with an essay section. */
export async function listPendingReviews(): Promise<PendingReviewAttempt[]> {
  await delay();
  return store
    .listAttempts({ status: "submitted" })
    .map((attempt) => {
      const exam = store.getExam(attempt.exam_id)!;
      const essayEQs = essayExamQuestionsFor(exam.id);
      const reviews = store.listEssayReviews(attempt.id);
      const pending = essayEQs.length - reviews.filter((r) => r.status === "completed").length;
      return {
        attempt_id: attempt.id,
        exam_id: exam.id,
        exam_title: exam.title,
        student_id: attempt.student_id,
        attempt_number: attempt.attempt_number,
        submitted_at: attempt.submitted_at,
        pending_essay_count: Math.max(pending, 0),
        total_essay_count: essayEQs.length,
      };
    })
    .sort((a, b) => (a.submitted_at ?? "").localeCompare(b.submitted_at ?? ""));
}

export interface ReviewAttachment {
  id: string;
  file_type: AttachmentFileType;
  file_name: string;
  file_url: string;
}

export interface ReviewQuestion {
  exam_question_id: string;
  question_id: string;
  type: QuestionType;
  prompt: string;
  points_possible: number;
  // multiple choice — already graded, shown read-only for context
  selected_option_text?: string | null;
  is_correct?: boolean | null;
  points_awarded?: number | null;
  // essay
  student_answer_text?: string | null;
  model_answer?: string | null;
  grading_rubric?: string | null;
  attachments?: ReviewAttachment[];
  review_id?: string;
  review_status?: "pending" | "in_review" | "completed";
  existing_score?: number | null;
  existing_feedback?: string | null;
}

export interface AttemptForReview {
  attempt_id: string;
  attempt_status: "submitted" | "graded";
  exam_title: string;
  student_id: string;
  attempt_number: number;
  sections: { id: string; title: string; section_type: QuestionType; questions: ReviewQuestion[] }[];
  ready_to_publish: boolean;
  already_published: boolean;
}

export async function getAttemptForReview(attemptId: string): Promise<AttemptForReview> {
  await delay();
  const attempt = store.getAttempt(attemptId);
  if (!attempt) throw new Error("Attempt not found.");
  const exam = store.getExam(attempt.exam_id)!;

  const sections = store.getExamSections(exam.id).map((s) => ({
    id: s.id,
    title: s.title,
    section_type: s.section_type,
    questions: store.getExamQuestions(s.id).map((eq): ReviewQuestion => {
      const q = store.getQuestion(eq.question_id)!;
      const points = store.pointsForExamQuestion(eq);
      const sa = store.getStudentAnswer(attemptId, eq.id);

      if (q.type === "multiple_choice") {
        const selected = sa?.selected_answer_id
          ? store.getMcAnswers(q.id).find((a) => a.id === sa.selected_answer_id)
          : undefined;
        return {
          exam_question_id: eq.id,
          question_id: q.id,
          type: q.type,
          prompt: q.prompt,
          points_possible: points,
          selected_option_text: selected?.answer_text ?? null,
          is_correct: sa?.is_correct ?? null,
          points_awarded: sa?.points_awarded ?? null,
        };
      }

      const essay = store.getEssayDetail(q.id);
      const review = sa ? store.getEssayReviewByAnswer(sa.id) : undefined;
      const attachments = sa
        ? store.listAttachments(sa.id).map(({ id, file_type, file_name, file_url }) => ({
            id,
            file_type,
            file_name,
            file_url,
          }))
        : [];
      return {
        exam_question_id: eq.id,
        question_id: q.id,
        type: q.type,
        prompt: q.prompt,
        points_possible: points,
        student_answer_text: sa?.essay_answer_text ?? "",
        model_answer: essay?.model_answer ?? null,
        grading_rubric: essay?.grading_rubric ?? null,
        attachments,
        review_id: review?.id,
        review_status: review?.status,
        existing_score: review?.score ?? null,
        existing_feedback: review?.feedback ?? null,
      };
    }),
  }));

  const alreadyPublished = attempt.status === "graded";
  return {
    attempt_id: attempt.id,
    attempt_status: alreadyPublished ? "graded" : "submitted",
    exam_title: exam.title,
    student_id: attempt.student_id,
    attempt_number: attempt.attempt_number,
    sections,
    ready_to_publish: !alreadyPublished && allEssaysReviewedForAttempt(attemptId),
    already_published: alreadyPublished,
  };
}

export interface SubmitReviewInput {
  reviewId: string;
  score: number;
  feedback: string;
  pointsPossible: number;
}

export interface SubmitReviewResult {
  readyToPublish: boolean;
}

/** Saves one essay's score + feedback. Does not publish the grade — see `publishGrade`. */
export async function submitEssayReview(input: SubmitReviewInput): Promise<SubmitReviewResult> {
  await delay();
  if (input.score < 0 || input.score > input.pointsPossible) {
    throw new Error(`Score must be between 0 and ${input.pointsPossible}.`);
  }
  const { readyToPublish } = reviewEssayAnswer(input.reviewId, { score: input.score, feedback: input.feedback });
  return { readyToPublish };
}

export interface PublishGradeResult {
  total_score: number;
  max_score: number;
  percentage: number;
  passed: boolean;
}

/**
 * Explicit "Publish grade" action. Computes and stores the final
 * grade and moves the attempt to 'graded' — this is what makes the
 * result visible on the student's Results page. Throws if any essay
 * on the attempt still lacks a completed review.
 */
export async function publishGrade(attemptId: string): Promise<PublishGradeResult> {
  await delay();
  return publishGradeService(attemptId);
}
